import React, { useState } from 'react';
import { Card, Form, Input, Button, Select, message, Typography } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../api';

const { Text } = Typography;

type InquiryType = 'athlete' | 'federation';

interface ContactUsFormValues {
  inquiry_type: InquiryType;
  name: string;
  email: string;
  organization?: string;
  message: string;
}

const INQUIRY_OPTIONS = [
  { value: 'athlete' as const, label: 'Athlete — I want to help or give feedback' },
  { value: 'federation' as const, label: 'Federation, club, or community' },
];

/**
 * Public contact-us form for the marketing landing page (no Firebase auth).
 * Submissions appear under Admin → Contact submissions.
 */
export const ContactUsForm: React.FC = () => {
  const [form] = Form.useForm<ContactUsFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values: ContactUsFormValues) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/public/contact-us`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inquiry_type: values.inquiry_type,
          name: values.name.trim(),
          email: values.email.trim(),
          organization: (values.organization || '').trim(),
          message: values.message.trim(),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        message.error(data.error || 'Could not send your message. Please try again.');
        return;
      }
      message.success('Thank you — we will get back to you soon.');
      form.resetFields();
      form.setFieldsValue({ inquiry_type: 'athlete' });
    } catch {
      message.error('Network error. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      className="landing-feature-card"
      style={{ borderRadius: 14 }}
      title={(
        <span>
          <SendOutlined style={{ marginRight: 8, color: 'var(--blue-bell)' }} aria-hidden />
          Contact us
        </span>
      )}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Tell us how you would like to help or collaborate. We read every message.
      </Text>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ inquiry_type: 'athlete' as InquiryType }}
        onFinish={onFinish}
        requiredMark="optional"
      >
        <Form.Item
          name="inquiry_type"
          label="I am reaching out as"
          rules={[{ required: true, message: 'Please choose one option' }]}
        >
          <Select
            options={INQUIRY_OPTIONS}
            aria-label="Inquiry type"
          />
        </Form.Item>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter your name' }]}
        >
          <Input maxLength={120} autoComplete="name" placeholder="Your name" />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Enter a valid email' },
          ]}
        >
          <Input maxLength={254} autoComplete="email" placeholder="you@example.com" />
        </Form.Item>
        <Form.Item name="organization" label="Organization (optional)">
          <Input
            maxLength={200}
            placeholder="Club, federation, or group name"
            aria-label="Organization optional"
          />
        </Form.Item>
        <Form.Item
          name="message"
          label="Message"
          rules={[
            { required: true, message: 'Please enter a message' },
            { min: 10, message: 'Please write at least a few words (10+ characters)' },
          ]}
        >
          <Input.TextArea
            rows={5}
            maxLength={4000}
            showCount
            placeholder="How you would like to help, or how we could work together"
            aria-label="Your message"
          />
        </Form.Item>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
          By submitting, you agree we may use your details to respond. See our{' '}
          <Link to="/privacy">Privacy Policy</Link>.
        </Text>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            htmlType="submit"
            loading={submitting}
            className="landing-live-jump-btn"
            aria-label="Send contact message"
          >
            Send message
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};
